import React, { useState, useEffect } from 'react';
import { Euro, Loader2, Save, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  windows: 'Service Windows',
  pack: 'Pack Complet',
  bios: 'Optimisation BIOS',
  aqmoptitool: 'AqmOptiTool (outil)',
  licence_1: 'Licence — 1 utilisation',
  licence_2: 'Licence — 2 utilisations',
  licence_3: 'Licence — 3 utilisations',
  licence_4: 'Licence — 4 utilisations',
  licence_5: 'Licence — 5 utilisations',
  licence_lifetime: 'Licence — Lifetime',
};

const PricingTab = ({ token }) => {
  const [prices, setPrices] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/pricing`)
      .then(r => r.json())
      .then(d => setPrices(Object.fromEntries(Object.entries(d.prices).map(([k, v]) => [k, String(v)]))))
      .catch(() => setError('Erreur de chargement'));
  }, []);

  const save = async () => {
    setError('');
    const parsed = {};
    for (const [k, v] of Object.entries(prices)) {
      const n = parseFloat(String(v).replace(',', '.'));
      if (isNaN(n) || n <= 0) { setError(`Prix invalide pour "${LABELS[k]}"`); return; }
      parsed[k] = n;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prices: parsed })
      });
      if (!res.ok) throw new Error('Erreur de sauvegarde');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!prices) return <div className="text-center py-10"><Loader2 className="w-6 h-6 text-green-500 animate-spin mx-auto" /></div>;

  const services = ['windows', 'pack', 'bios', 'aqmoptitool'];
  const licences = ['licence_1', 'licence_2', 'licence_3', 'licence_4', 'licence_5', 'licence_lifetime'];

  const Field = ({ k }) => (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-gray-300 text-sm">{LABELS[k]}</span>
      <div className="relative flex-shrink-0">
        <input
          type="text"
          value={prices[k]}
          onChange={(e) => setPrices(p => ({ ...p, [k]: e.target.value }))}
          data-testid={`price-input-${k}`}
          className="w-24 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 pr-7 text-white text-sm text-right focus:outline-none focus:border-green-500"
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs">€</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 max-w-2xl" data-testid="pricing-tab">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-green-500/15 border border-green-500/30 flex items-center justify-center">
          <Euro className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h2 className="text-white font-bold text-base md:text-lg">Gestion des Prix</h2>
          <p className="text-gray-400 text-xs">Appliqué partout : site, Stripe, PayPal et tickets Discord. Suppléments prio/portable calculés automatiquement (+4€ Windows/BIOS, +8€ Pack).</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-2">Services</h3>
          <div className="divide-y divide-zinc-800/70">
            {services.map(k => <Field key={k} k={k} />)}
          </div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-2">Licences</h3>
          <div className="divide-y divide-zinc-800/70">
            {licences.map(k => <Field key={k} k={k} />)}
          </div>
        </div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}
      <button
        onClick={save}
        disabled={saving}
        data-testid="pricing-save-btn"
        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <RotateCcw className="w-4 h-4" /> : <Save className="w-4 h-4" />}
        {saved ? 'Enregistré ✓' : 'Enregistrer les prix'}
      </button>
    </div>
  );
};

export default PricingTab;
